/**
 * StrategyStalenessBadge — Phase V (V4) "inputs drifted" signal.
 *
 * Warning chip (signal, NOT an error — never blocks editing) with a popover
 * breakdown of the drifted channels and two reactions sanctioned by
 * input-versioning.md §4: keep the experiment frozen, or fork on fresh inputs.
 * Hidden when the verdict is fresh or the strategy has no inputManifest.
 */
import React, { useState } from 'react';
import { Box, Button, Chip, Popover, Typography, Divider } from '@mui/material';
import { HistoryToggleOff as DriftIcon } from '@mui/icons-material';
import type { CliStrategyStaleness } from '@modules/shared-types';

export interface StrategyStalenessBadgeProps {
  verdict: CliStrategyStaleness | null;
  /** Optional on read-only surfaces (e.g. the onboarding editor header, D3). */
  onFork?: () => void;
  /** Spec D3 (AC-D3-6): opens the insight impact tree scoped to pinned → latest. */
  onWhatChanged?: () => void;
}

const shortId = (id?: string): string => (id ? `${id.slice(0, 6)}…` : '?');

export const StrategyStalenessBadge: React.FC<StrategyStalenessBadgeProps> = ({ verdict, onFork, onWhatChanged }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!verdict?.stale) return null;

  const close = () => setAnchorEl(null);

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        color="warning"
        icon={<DriftIcon />}
        label="Inputs drifted"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="strategy-staleness-badge"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 380 } } }}
        data-testid="strategy-staleness-popover"
      >
        <Typography variant="subtitle2">Inputs drifted since this strategy was generated</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          The experiment still runs on its pinned snapshot. Editing is not blocked.
        </Typography>
        {verdict.insightsAnalysis.drifted && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2">
              {/* D3: lineage-aware copy "built on vN — vM available" when labels exist. */}
              {verdict.insightsAnalysis.lineageAware && verdict.insightsAnalysis.latestVersionLabel
                ? `Built on ${verdict.insightsAnalysis.pinnedVersionLabel ?? shortId(verdict.insightsAnalysis.pinned)} — ${verdict.insightsAnalysis.latestVersionLabel} available`
                : 'Newer insights analysis available'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              pinned {shortId(verdict.insightsAnalysis.pinned)} → latest {shortId(verdict.insightsAnalysis.latestCompleted)}
            </Typography>
            {onWhatChanged && (
              <Button
                size="small"
                sx={{ display: 'block', px: 0, mt: 0.25 }}
                onClick={() => { close(); onWhatChanged(); }}
                data-testid="staleness-what-changed-btn"
              >
                What changed
              </Button>
            )}
          </Box>
        )}
        {verdict.variableSet.drifted && (
          <>
            <Divider />
            <Box sx={{ py: 1 }}>
              <Typography variant="body2">
                Variable set updated (v{verdict.variableSet.pinned} → v{verdict.variableSet.current})
              </Typography>
            </Box>
          </>
        )}
        {verdict.tasks.drifted && (
          <>
            <Divider />
            <Box sx={{ py: 1 }}>
              <Typography variant="body2">
                {verdict.tasks.changed.length > 0 && `${verdict.tasks.changed.length} task(s) changed`}
                {verdict.tasks.changed.length > 0 && verdict.tasks.deleted.length > 0 && ', '}
                {verdict.tasks.deleted.length > 0 && `${verdict.tasks.deleted.length} deleted`}
              </Typography>
            </Box>
          </>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button size="small" onClick={close} data-testid="staleness-keep-frozen-btn">
            Keep frozen
          </Button>
          {onFork && (
            <Button
              size="small"
              variant="contained"
              onClick={() => { close(); onFork?.(); }}
              data-testid="staleness-fork-btn"
            >
              Fork on fresh inputs…
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Forking creates a successor strategy pinned to fresh inputs. The original is never modified.
        </Typography>
      </Popover>
    </>
  );
};
